// CloudKeeper Org Services Portal — Security Hub API client.
// Uses portalFetch from portalApi.js (JWT stored as portal_token).
import { portalFetch } from './portalApi';

// --- config ---
export const getSecurityHubConfig = () =>
  portalFetch('/api/v1/securityhub/config');

export const enableSecurityHub = (body) =>
  portalFetch('/api/v1/securityhub/config', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const disableSecurityHub = () =>
  portalFetch('/api/v1/securityhub/config', { method: 'DELETE' });

export const updateStandards = (enabledStandards) =>
  portalFetch('/api/v1/securityhub/config/standards', {
    method: 'PUT',
    body: JSON.stringify({ enabledStandards }),
  });

export const updateRegions = (linkingRegions) =>
  portalFetch('/api/v1/securityhub/config/regions', {
    method: 'PUT',
    body: JSON.stringify({ linkingRegions }),
  });

// --- members ---
export const listMembers = () =>
  portalFetch('/api/v1/securityhub/members');

// Candidate AWS accounts for enrollment. Served under the securityhub service so a
// portal user with only securityhub access (no cloudtrail) can manage enrollment.
export const listSecurityHubAccounts = () =>
  portalFetch('/api/v1/securityhub/accounts');

export const addMembers = (accountIds) =>
  portalFetch('/api/v1/securityhub/members', {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });

export const removeMember = (accountId) =>
  portalFetch(`/api/v1/securityhub/members/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
  });

export const retryMember = (accountId) =>
  portalFetch(`/api/v1/securityhub/members/${encodeURIComponent(accountId)}/retry`, {
    method: 'POST',
  });

// Repairs members associated with the admin in only some regions (associates the
// missing (member, region) pairs). Async — poll config/members after.
export const reconcileAssociations = () =>
  portalFetch('/api/v1/securityhub/members/reconcile', {
    method: 'POST',
  });

// --- refresh ---
export const triggerRefresh = () =>
  portalFetch('/api/v1/securityhub/refresh', { method: 'POST' });

export const getRefreshJob = (jobId) =>
  portalFetch(`/api/v1/securityhub/refresh/${encodeURIComponent(jobId)}`);

// Currently running refresh job for this tenant (data: null when none) — lets
// the dashboard re-attach its progress bar after navigation or a page reload.
export const getActiveRefreshJob = () =>
  portalFetch('/api/v1/securityhub/refresh/active');

// --- findings ---
export const listFindings = (filters = {}) => {
  const qs = new URLSearchParams();
  if (filters.severity?.length) qs.set('severity', filters.severity.join(','));
  if (filters.workflowStatus?.length) qs.set('workflowStatus', filters.workflowStatus.join(','));
  if (filters.complianceStatus?.length) qs.set('complianceStatus', filters.complianceStatus.join(','));
  if (filters.account?.length) qs.set('account', filters.account.join(','));
  if (filters.standard?.length) qs.set('standard', filters.standard.join(','));
  if (filters.search) qs.set('search', filters.search);
  if (filters.cursor) qs.set('cursor', filters.cursor);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const q = qs.toString();
  return portalFetch(`/api/v1/securityhub/findings${q ? `?${q}` : ''}`);
};

export const getFinding = (findingId) =>
  portalFetch(`/api/v1/securityhub/findings/${encodeURIComponent(findingId)}`);

export const updateFindingWorkflow = (findingIds, workflowStatus, note) =>
  portalFetch('/api/v1/securityhub/findings/workflow', {
    method: 'POST',
    body: JSON.stringify({ findingIds, workflowStatus, note: note || undefined }),
  });

// --- summary + meta ---
// account/region (optional) scope the per-standard scores to one combo.
export const getComplianceSummary = (account, region) => {
  const qs = new URLSearchParams();
  if (account) qs.set('account', account);
  if (region) qs.set('region', region);
  const q = qs.toString();
  return portalFetch(`/api/v1/securityhub/compliance-summary${q ? `?${q}` : ''}`);
};

export const listStandards = () =>
  portalFetch('/api/v1/securityhub/standards');

// --- umbrella ---
export const getCapabilities = () =>
  portalFetch('/api/v1/securityhub/capabilities');

export const getOverview = () =>
  portalFetch('/api/v1/securityhub/overview');

export const listAllFindings = (params = {}) => {
  const qs = new URLSearchParams();
  // Repeated params with NO "[]" suffix — the backend reads q["engine"], q["severity"], etc.
  if (params.engine?.length) params.engine.forEach((v) => qs.append('engine', v));
  if (params.severity?.length) params.severity.forEach((v) => qs.append('severity', v));
  if (params.workflowStatus?.length) params.workflowStatus.forEach((v) => qs.append('workflowStatus', v));
  if (params.complianceStatus?.length) params.complianceStatus.forEach((v) => qs.append('complianceStatus', v));
  // account is a single value; FindingsList passes it as a 1-element array, so unwrap it.
  const account = Array.isArray(params.account) ? params.account[0] : params.account;
  if (account) qs.set('account', account);
  if (params.search) qs.set('search', params.search);
  if (params.findingType) qs.set('type', params.findingType);
  if (params.standard) qs.set('standard', params.standard);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return portalFetch(`/api/v1/securityhub/findings/all${q ? `?${q}` : ''}`);
};

// --- AWS Config managed capability ---
export const getConfigStatus = () =>
  portalFetch('/api/v1/securityhub/config-recorders/status');

export const refreshConfigRecorders = () =>
  portalFetch('/api/v1/securityhub/config-recorders/refresh', { method: 'POST' });

export const getConfigRefreshJob = (jobId) =>
  portalFetch(`/api/v1/securityhub/config-recorders/refresh/${encodeURIComponent(jobId)}`);

export const enableConfigRecorders = (cells, scope) =>
  portalFetch('/api/v1/securityhub/config-recorders/enable', {
    method: 'POST',
    body: JSON.stringify({ cells, scope }),
  });

export const disableConfigRecorders = (cells) =>
  portalFetch('/api/v1/securityhub/config-recorders/disable', {
    method: 'POST',
    body: JSON.stringify({ cells }),
  });

export const deleteConfigRecorders = (cells) =>
  portalFetch('/api/v1/securityhub/config-recorders/delete', {
    method: 'POST',
    body: JSON.stringify({ cells }),
  });

export const setConfigScope = (cells, scope) =>
  portalFetch('/api/v1/securityhub/config-recorders/scope', {
    method: 'POST',
    body: JSON.stringify({ cells, scope }),
  });

// --- role permissions ("Fix roles") ---
// Prism keeps a scoped inline policy on each enrolled account's switch role. The
// scan is async: POST starts (or re-joins) a job, GET polls it. Fix is
// fire-and-forget — re-run a scan to see the outcome.
export const startRolesScan = () =>
  portalFetch('/api/v1/securityhub/roles/status', { method: 'POST' });

export const getRolesJob = (jobId) =>
  portalFetch(`/api/v1/securityhub/roles/status/${encodeURIComponent(jobId)}`);

export const fixRoles = () =>
  portalFetch('/api/v1/securityhub/roles/fix', { method: 'POST' });

// AWS Config capability's own grant (same three routes under /config-recorders).
export const startConfigRolesScan = () =>
  portalFetch('/api/v1/securityhub/config-recorders/roles/status', { method: 'POST' });

export const getConfigRolesJob = (jobId) =>
  portalFetch(`/api/v1/securityhub/config-recorders/roles/status/${encodeURIComponent(jobId)}`);

export const fixConfigRoles = () =>
  portalFetch('/api/v1/securityhub/config-recorders/roles/fix', { method: 'POST' });

// --- CSPM controls hierarchy ---
export const getStandardControls = (standard, params = {}) => {
  const qs = new URLSearchParams();
  qs.set('standard', standard);
  if (params.status) qs.set('status', params.status);
  if (params.search) qs.set('search', params.search);
  return portalFetch(`/api/v1/securityhub/standard-controls?${qs.toString()}`);
};

export const getControlChecks = (controlId, params = {}) => {
  const qs = new URLSearchParams();
  qs.set('generator', `security-control/${controlId}`);
  if (params.complianceStatus?.length) qs.set('complianceStatus', params.complianceStatus.join(','));
  if (params.workflowStatus?.length) qs.set('workflowStatus', params.workflowStatus.join(','));
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  return portalFetch(`/api/v1/securityhub/findings?${qs.toString()}`);
};

export const getConfigRules = (account, region) => {
  const qs = new URLSearchParams();
  if (account) qs.set('account', account);
  if (region) qs.set('region', region);
  return portalFetch(`/api/v1/securityhub/config-rules?${qs.toString()}`);
};
